import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { FitStepProgress } from './FitStepProgress';
import { ProductData, RetailerData, ModalContext } from '../types';
import { X, ShoppingBag, ArrowLeft, ArrowRight, Info, Camera, Upload, ChevronLeft, ChevronRight, Sparkles, Coffee, Dumbbell, Briefcase, Share2, Download as DownloadIcon, Maximize2, ThumbsUp, ThumbsDown, Heart, RotateCw, UserCircle, Building2, Cake, Film, Globe, Home, Image as ImageIcon, Sun, Moon, Users, User, Video, Trash2 } from 'lucide-react';

interface WizardViewProps {
  productData: ProductData;
  retailerData: RetailerData;
  apiUrl: string;
  context?: ModalContext;
  category?: string;
  brandingHideLogo?: boolean;
}
interface WizardItem {
  title: string;
  price?: string;
  imageUrl?: string;
  imageUrls?: string[];
  currentImageIndex?: number;
  url?: string;
  isUploaded?: boolean;
}

interface StyleOption { id: string; name: string; desc?: string; icon?: string; color?: string }

export const WizardView: React.FC<WizardViewProps> = ({ productData, retailerData, apiUrl, context, category, brandingHideLogo }) => {
  const { i18n } = useTranslation();
  const L = (nl: string, en: string) => (i18n.language?.startsWith('nl') ? nl : en);
  const CURRENT_TERMS_VERSION = 'v1';
  const isPortal = !!(context && (context as any).isPortalEmbed);
  const [step, setStep] = useState<1|2|3>(1);
  const [items, setItems] = useState<WizardItem[]>(() => {
    const p: any = productData || {};
    const imgs = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
    const hasData = !!(
      (p.title && String(p.title).trim()) ||
      (p.price && String(p.price).trim()) ||
      (p.imageUrl && String(p.imageUrl).trim()) ||
      (p.url && String(p.url).trim()) ||
      imgs.length
    );
    if (!hasData) return [];
    return [{
      title: p.title,
      price: p.price,
      imageUrl: p.imageUrl,
      imageUrls: imgs.length ? imgs : (p.imageUrl ? [p.imageUrl] : []),
      currentImageIndex: 0,
      url: p.url,
    }];
  });
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  // Multiple pasphoto slots
  const [photoSlots, setPhotoSlots] = useState<string[]>([]);
  const [photoMap, setPhotoMap] = useState<Record<string, string>>({});
  const [uploadSlot, setUploadSlot] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string>(''); // legacy single support
  const [style, setStyle] = useState<string>('1');
  const [styleOptions, setStyleOptions] = useState<StyleOption[]>([]);
  const [slotLabels, setSlotLabels] = useState<Record<string, string>>({});
  const [categoryInfoBox, setCategoryInfoBox] = useState<string>('');
  const [infoHeader, setInfoHeader] = useState<string>('');
  const [infoBody, setInfoBody] = useState<string>('');
  const [tipHeaderTitle, setTipHeaderTitle] = useState<string>('');
  const [tipBodyText, setTipBodyText] = useState<string>('');
  const [requiredSlots, setRequiredSlots] = useState<string[]>([]);
  const [limits, setLimits] = useState<{ maxItemsGuest?: number | null; maxItemsRegistered?: number | null; guestCanChooseStyle?: boolean | null } | null>(null);
  const [perStylePhotoOrder, setPerStylePhotoOrder] = useState<Record<string, string[]>>({});
  const [defaultPhotoOrder, setDefaultPhotoOrder] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [quota, setQuota] = useState<{ remaining?: number | null; maxSessions?: number | null } | null>(null);
  const [quotaSource, setQuotaSource] = useState<'guest' | 'user' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string>('');
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>('');
  const [satisfied, setSatisfied] = useState<boolean | null>(null);
  const [favorite, setFavorite] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showTip, setShowTip] = useState<string | null>(null);
  const lottieRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const productFileInputRef = useRef<HTMLInputElement | null>(null);
  // Prevent double-opening the file dialog due to multiple click sources
  const fileDialogGuardRef = useRef<boolean>(false);
  const fileDialogGuardTimerRef = useRef<number | null>(null);
  const [isUploadingProduct, setIsUploadingProduct] = useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [feedbackInput, setFeedbackInput] = useState<string>('');
  const [showFeedbackInfo, setShowFeedbackInfo] = useState<boolean>(false);
  const [isScraping, setIsScraping] = useState<boolean>(false);
  const [consentGiven, setConsentGiven] = useState<boolean>(false);
  const lastSelectedImageUrlRef = useRef<string | null>(null);
  const currentUploadSlotRef = useRef<string | null>(null);

  // Track when the result image has fully loaded to adjust temporary spacing
  const [resultImageLoaded, setResultImageLoaded] = useState<boolean>(false);

  const PLACEHOLDER_URL = 'https://hruleghaabwolyrkzzoc.supabase.co/storage/v1/object/public/logos/FiT_just_logo_blank.png';

  // Ensure lottie-web is available for loading animations (portal + retailer)
  useEffect(() => {
    try {
      if (!(window as any).lottie) {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js';
        s.async = true;
        document.head.appendChild(s);
      }
    } catch {}
  }, []);

  // Preload placeholder to make it show instantly when switching to result view
  useEffect(() => {
    try {
      const img = new Image();
      img.src = PLACEHOLDER_URL;
    } catch {}
  }, []);

  // Reorder photo slots when style changes according to category-config (perStylePhotoOrder/defaultPhotoOrder)
  useEffect(() => {
    try {
      const order = (perStylePhotoOrder && perStylePhotoOrder[String(style)]) || defaultPhotoOrder || [];
      if (!order.length) return;
      setPhotoSlots(prev => {
        const setPrev = new Set(prev);
        const ordered = order.filter(id => setPrev.has(id));
        const rest = prev.filter(id => ordered.indexOf(id) === -1);
        return [...ordered, ...rest];
      });
    } catch {}
  }, [style, perStylePhotoOrder, defaultPhotoOrder]);

  // PasPhoto labels (fallbacks if not provided by category-config)
  const SLOT_LABELS_NL: Record<string, string> = {
    pasphoto_front: 'Foto van je gezicht recht van voren, met neutrale achtergrond.',
    pasphoto_side: 'Foto van je gezicht vanaf de zijkant, profiel goed zichtbaar.',
    pasphoto_fullbody_front: 'Volledige lichaamsfoto recht van voren, in natuurlijke houding.',
    pasphoto_fullbody_side: 'Volledige lichaamsfoto vanaf de zijkant, ontspannen houding.',
    pasphoto_spouse: 'Foto van je partner recht van voren, vergelijkbaar met jouw pasfoto.',
    pasphoto_member1: 'Foto van het eerste gezinslid dat je toevoegt.',
    pasphoto_member2: 'Foto van het tweede gezinslid dat je toevoegt.',
    pasphoto_member3: 'Foto van het derde gezinslid dat je toevoegt.',
    pasphoto_member4: 'Foto van het vierde gezinslid dat je toevoegt.',
    pasphoto_room_1: 'Foto van een kamer of ruimte voor interieurvisualisatie.'
  }

  function onRetryToStyle() {
    try { setResultUrl(''); } catch {}
    try { setResultImageLoaded(false); } catch {}
    setStep(3 as 1|2|3);
  }

  // Persist guest photos for the browser session
  useEffect(() => {
    try {
      const loggedIn = (() => { try { return sessionStorage.getItem('fit_logged_in') === 'true'; } catch { return false; } })();
      if (!loggedIn) {
        const raw = sessionStorage.getItem('fit_guest_photo_map');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object' && Object.keys(photoMap).length === 0) {
            setPhotoMap(parsed as any);
          }
        }
      }
    } catch {}
    // run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try {
      const loggedIn = (() => { try { return sessionStorage.getItem('fit_logged_in') === 'true'; } catch { return false; } })();
      if (!loggedIn) sessionStorage.setItem('fit_guest_photo_map', JSON.stringify(photoMap));
    } catch {}
  }, [photoMap]);

  // Open login view in parent modal
  function openLogin() {
    try { sessionStorage.removeItem('fit_guest_selected'); } catch {}
    try { window.parent.postMessage({ type: 'fit-open-login' }, '*'); } catch {}
    try { window.dispatchEvent(new Event('fit-open-login' as any)); } catch {}
  }

  // Toggle favorite for current session (result step)
  async function onToggleFavorite() {
    try {
      const next = !favorite;
      setFavorite(next); // optimistic UI
      if (!sessionId) {
        setShowTip('Sessie nog niet beschikbaar');
        setTimeout(() => setShowTip(null), 1500);
        return;
      }
      const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const r = await fetch(`${apiUrl}/api/consumer/fit-sessions/${encodeURIComponent(sessionId)}/favorite`, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify({ favorite: next })
      });
      if (!r.ok) {
        // revert on failure
        setFavorite(v => !v);
        setShowTip(L('Favoriet bijwerken mislukt','Failed to update favorite'));
        setTimeout(() => setShowTip(null), 1600);
      } else {
        setShowTip(next ? L('Toegevoegd aan favorieten','Added to favorites') : L('Verwijderd uit favorieten','Removed from favorites'));
        setTimeout(() => setShowTip(null), 1200);
      }
    } catch {
      setFavorite(v => !v);
      setShowTip(L('Favoriet bijwerken mislukt','Failed to update favorite'));
      setTimeout(() => setShowTip(null), 1600);
    }
  }
  const SLOT_LABELS_EN: Record<string, string> = {
    pasphoto_front: 'Front-facing photo of your face with a neutral background.',
    pasphoto_side: 'Side-view photo of your face showing your profile clearly.',
    pasphoto_fullbody_front: 'Full-body photo from the front in a natural stance.',
    pasphoto_fullbody_side: 'Full-body photo from the side in a relaxed posture.',
    pasphoto_spouse: 'Front-facing photo of your partner, similar to your own portrait.',
    pasphoto_member1: 'Photo of the first family member you add.',
    pasphoto_member2: 'Photo of the second family member you add.',
    pasphoto_member3: 'Photo of the third family member you add.',
    pasphoto_member4: 'Photo of the fourth family member you add.',
    pasphoto_room_1: 'Photo of a room or space for interior visualization.'
  };
  function normSlotId(id: string): string {
    return String(id || '').trim().toLowerCase();
  }
  function getSlotLabel(slotId: string): string {
    const k = normSlotId(slotId);
    const plain = k.replace(/^pasphoto_/, '');
    const fallback = (i18n.language && i18n.language.startsWith('nl')) ? SLOT_LABELS_NL : SLOT_LABELS_EN;
    return (
      slotLabels[slotId] ||
      slotLabels[k] ||
      slotLabels[plain] ||
      fallback[k] ||
      fallback[plain] ||
      slotId
    );
  }
  function slotIdToType(slotId: string): string {
    return String(slotId || '').replace(/^pasphoto_/i, '');
  }
  const hasAnyUserPhoto = useMemo(() => {
    try { return Object.values(photoMap).some(Boolean) || !!photoUrl; } catch { return !!photoUrl; }
  }, [photoMap, photoUrl]);
  const termsUrl = useMemo(() => {
    const lang = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
    return `https://fit.brendr.io/terms?lang=${lang}`;
  }, [i18n.language]);
  const privacyUrl = useMemo(() => {
    const lang = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
    return `https://fit.brendr.io/privacy?lang=${lang}`;
  }, [i18n.language]);

  // Persisted login bootstrap and quota refresh helpers
  async function refreshQuotaFor(userIdParam?: string | null) {
    try {
      const deviceId = getDeviceId();
      const params = new URLSearchParams();
      if (context?.shopId) params.set('shopId', String(context.shopId));
      if (userIdParam) params.set('userId', String(userIdParam)); else params.set('deviceId', deviceId);
      const qr = await fetch(`${apiUrl}/api/widget/quota?${params.toString()}`);
      const qj = await qr.json().catch(() => ({}));
      if (qj && qj.success) {
        setQuota({ remaining: qj.remaining ?? null, maxSessions: qj.maxSessions ?? null });
        setQuotaSource(userIdParam ? 'user' : 'guest');
      }
    } catch {}
  }
  function updateFromUser(user: any) {
    if (!user) return;
    try { sessionStorage.removeItem('fit_guest_selected'); } catch {}
    try { sessionStorage.setItem('fit_logged_in', 'true'); } catch {}
    const uid = String((user.id ?? user.user_id ?? user.userId ?? '') || '');
    const email = String((user.email ?? user.emailAddress ?? '') || '');
    if (uid) setUserId(uid);
    if (email) setUserEmail(email);
    try {
      const fn = (user.first_name || user.firstName || user.firstname || user.given_name || user.givenName || '') || '';
      if (fn) setUserFirstName(String(fn));
    } catch {}
    refreshQuotaFor(uid || null);
    // After login, refetch profile to populate pasPhotos (mobile fallback without cookies)
    try { loadUserProfilePhotos(uid || null); } catch {}
  }

  // Fetch profile with Authorization header fallback and apply pasPhoto fields
  async function loadUserProfilePhotos(uid?: string | null) {
    try {
      const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const r = await fetch(`${apiUrl}/api/consumer/profile`, { credentials: 'include', headers });
      const j = await r.json().catch(() => ({} as any));
      if (j && j.success && j.profile) {
        const pm: Record<string, string> = {};
        const prof = j.profile || {};
        try {
          const accAt = (prof as any)?.acceptedTermsAt || null;
          const tver = (prof as any)?.termsVersion || null;
          if (accAt && tver === CURRENT_TERMS_VERSION) setConsentGiven(true);
        } catch {}
        try {
          const fn = (prof.first_name || prof.firstName || prof.firstname || (prof.name ? String(prof.name).split(' ')[0] : '')) || '';
          if (fn) setUserFirstName(String(fn));
        } catch {}
        const read = (k: string) => (prof[k] ?? prof[k.toLowerCase()] ?? null);
        const pairs: Array<[string, any]> = [
          ['pasphoto_front', read('pasPhoto_front')],
          ['pasphoto_side', read('pasPhoto_side')],
          ['pasphoto_fullbody_front', read('pasPhoto_fullBody_front') || read('pasPhoto_fullbody_front')],
          ['pasphoto_fullbody_side', read('pasPhoto_fullBody_side') || read('pasPhoto_fullbody_side')],
          ['pasphoto_spouse', read('pasPhoto_spouse')],
          ['pasphoto_member1', read('pasPhoto_member1')],
          ['pasphoto_member2', read('pasPhoto_member2')],
          ['pasphoto_member3', read('pasPhoto_member3')],
          ['pasphoto_member4', read('pasPhoto_member4')],
          ['pasphoto_room_1', read('pasPhoto_room_1')],
        ];
        for (const [id, v] of pairs) if (v) pm[id] = String(v);
        if (Object.keys(pm).length) setPhotoMap(pm);
        const pf = read('pasPhoto_front');
        if (pf && !photoUrl) setPhotoUrl(String(pf));
      }
    } catch {}
  }

  const canPrev = step > 1 && !resultUrl;
  const canNext = useMemo(() => {
    if (resultUrl) return false;
    if (step === 1) return items.length > 0;
    if (step === 2) {
      const hasAny = Object.values(photoMap).some(Boolean) || !!photoUrl;
      // If required slots are configured, require at least one of them
      let ok = hasAny;
      if (requiredSlots.length) {
        const hasRequired = requiredSlots.some((slotId) => !!photoMap[slotId] || (!!photoUrl && (slotId === 'pasphoto_front' || slotId === 'front' || slotId === 'pasPhoto_front')));
        ok = hasAny && hasRequired;
      }
      // Require consent before continuing from step 2
      return ok && !!consentGiven;
    }
    return true;
  }, [resultUrl, step, items.length, photoMap, photoUrl, requiredSlots, consentGiven]);

  // Per-category max items (guest vs registered)
  const itemsCap = useMemo(() => {
    const maxGuest = Number(limits?.maxItemsGuest ?? 0);
    const maxReg = Number(limits?.maxItemsRegistered ?? 3);
    return 1 + (userEmail ? maxReg : maxGuest);
  }, [limits, userEmail]);
 
  // Display value for UI copy: show the configured maximum from category settings
  // (registered users see limits.maxItemsRegistered, guests see limits.maxItemsGuest)
  const itemsMaxFromConfig = useMemo(() => {
    const maxGuest = Number(limits?.maxItemsGuest ?? 0);
    const maxReg = Number(limits?.maxItemsRegistered ?? 3);
    return userEmail ? maxReg : maxGuest;
  }, [limits, userEmail]);
 
  async function isImageLoadable(src?: string, timeoutMs: number = 6000): Promise<boolean> {
    try {
      const url = String(src || '').trim();
      if (!url) return false;
      return await new Promise<boolean>((resolve) => {
        let settled = false;
        const img = new Image();
        const timer = setTimeout(() => {
          if (!settled) { settled = true; resolve(false); }
        }, Math.max(1000, timeoutMs));
        img.onload = () => {
          if (settled) return; settled = true; clearTimeout(timer);
          const ok = (img.naturalWidth || 0) > 0 && (img.naturalHeight || 0) > 0;
          resolve(ok);
        };
        img.onerror = () => {
          if (settled) return; settled = true; clearTimeout(timer);
          resolve(false);
        };
        try { img.referrerPolicy = 'no-referrer'; } catch {}
        img.src = url;
      });
    } catch {
      return false;
    }
  }

  async function onProductFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    try {
      const f = e.target.files && e.target.files[0];
      try { (e.target as HTMLInputElement).value = ''; } catch {}
      if (!f) return;
      const allowed = ['image/png', 'image/jpeg'];
      if (!allowed.includes(f.type)) {
        setShowTip(L('Alleen PNG of JPG toegestaan','Only PNG or JPG allowed'));
        setTimeout(() => setShowTip(null), 1800);
        return;
      }
      const MAX = 3 * 1024 * 1024;
      if (f.size > MAX) {
        setShowTip(L('Bestand mag niet groter zijn dan 3 MB','File may not be larger than 3 MB'));
        setTimeout(() => setShowTip(null), 2000);
        return;
      }
      setIsUploadingProduct(true);
      const fd = new FormData();
      fd.append('image', f);
      const r = await fetch(`${apiUrl}/api/widget/product/upload`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({} as any));
      setIsUploadingProduct(false);
      if (!r.ok || !j || !j.success || !j.url) {
        setShowTip(L('Upload mislukt','Upload failed'));
        setTimeout(() => setShowTip(null), 1800);
        return;
      }
      const url = String(j.url);
      setItems((prev) => prev.length >= itemsCap ? prev : [...prev, {
        title: '',
        imageUrl: url,
        imageUrls: [],
        currentImageIndex: 0,
        url: undefined,
        isUploaded: true
      }]);
      setShowAdd(false);
      setShowTip(L('Afbeelding toegevoegd','Image added'));
      setTimeout(() => setShowTip(null), 1200);
    } catch {
      setIsUploadingProduct(false);
      setShowTip(L('Upload mislukt','Upload failed'));
      setTimeout(() => setShowTip(null), 1800);
    }
  }

  function guardedProductUploadClick() {
    try {
      if (items.length >= itemsCap) return;
      productFileInputRef.current?.click();
    } catch {}
  }

  const onPrev = () => setStep((s) => (s > 1 ? ((s - 1) as 1|2|3) : s));
  const onNext = async () => {
    if (step === 1) {
      try {
        await new Promise<void>((resolve) => {
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve());
          } else {
            setTimeout(() => resolve(), 0);
          }
        });
      } catch {}
    }
    // Block progressing from step 1 if first item has no image
    if (step === 1) {
      const it = items[0];
      const imgs = it ? (Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : [])) : [];
      const cur = it && typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
      const candidate = imgs[cur] || it?.imageUrl || '';
      const imgSrc = String(lastSelectedImageUrlRef.current || candidate);
      const hasAnyImage = imgs.length > 0 && String(imgSrc || '').trim().length > 0;
      if (!hasAnyImage) {
        setShowTip(L('Dit product kan helaas niet mee in je FiT sessie. Probeer het met een ander product.','This product cannot be used in your FiT session. Please try another product.'));
        setTimeout(() => setShowTip(null), 2500);
        return;
      }
      const ok = await isImageLoadable(imgSrc);
      if (!ok) {
        setShowTip(L('De productafbeelding kon niet worden geladen. Sluit dit venster en start FiT opnieuw vanaf een productpagina','The product image could not be loaded. Close this window and start FiT again from a product page.'));
        setTimeout(() => setShowTip(null), 10000);
        return;
      }
    }
    // Prevent advancing from step 2 without consent and any photo and inform user
    if (step === 2) {
      if (!consentGiven) {
        setShowTip(L('Je moet akkoord gaan met de voorwaarden en het privacybeleid','You must agree to the terms and privacy policy'));
        setTimeout(() => setShowTip(null), 1800);
        return;
      }
      const hasAny = Object.values(photoMap).some(Boolean) || !!photoUrl;
      if (!hasAny) {
        setShowTip(L('Je dient eerst een pasfoto te uploaden voordat je verder kunt','Please upload a photo first before continuing'));
        setTimeout(() => setShowTip(null), 1800);
        return;
      }
    }
    setStep((s) => (s < 3 ? ((s + 1) as 1|2|3) : s));
  };

  // UI helpers
  const onCloseModal = () => {
    try {
      // Match host page listener (string) and keep backwards-compatible object messages
      window.parent.postMessage('close-fit-modal', '*');
      window.parent.postMessage({ type: 'close-fit-modal' } as any, '*');
      window.parent.postMessage({ type: 'FIT_CLOSE_MODAL' } as any, '*');
    } catch {}
  };
  const onLogout = async () => {
    // 1) Invalidate server session (clears HttpOnly cookie)
    try {
      await fetch(`${apiUrl}/api/auth-supabase/logout`, { method: 'POST', credentials: 'include' });
    } catch {}

    // 2) Clear any client-side tokens stored by previous flows
    try { sessionStorage.removeItem('fit_user_token'); sessionStorage.removeItem('fit_user'); sessionStorage.removeItem('fit_logged_in'); } catch {}

    // 3) Notify parent (retailer page) to clear widget-side session as well
    try { window.parent.postMessage({ type: 'fit-logout' }, '*'); } catch {}

    // 4) Reset local UI state to guest
    setUserEmail(null);
    setUserId(null);
    setUserFirstName(null);
    setStep(1);

    // 5) Refresh guest quota so the chip reflects new state
    try {
      const deviceId = getDeviceId();
      const params = new URLSearchParams();
      if (context?.shopId) params.set('shopId', String(context.shopId));
      params.set('deviceId', deviceId);
      const qr = await fetch(`${apiUrl}/api/widget/quota?${params.toString()}`, { credentials: 'include' });
      const qj = await qr.json().catch(() => ({}));
      if (qj && qj.success) {
        setQuota({ remaining: qj.remaining ?? null, maxSessions: qj.maxSessions ?? null });
      }
    } catch {}
  };
  const sessionsUsed = useMemo(() => {
    if (quota?.maxSessions == null || quota?.remaining == null) return null;
    return Math.max(0, Number(quota.maxSessions) - Number(quota.remaining));
  }, [quota?.maxSessions, quota?.remaining]);

  const displayFirstName = useMemo(() => {
    if (userFirstName && String(userFirstName).trim()) return String(userFirstName).trim();
    try {
      const raw = sessionStorage.getItem('fit_user');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.firstName) return String(parsed.firstName);
      }
    } catch {}
    if (userEmail) {
      const local = String(userEmail).split('@')[0] || '';
      const guess = local.split(/[._-]/)[0] || local;
      return guess.charAt(0).toUpperCase() + guess.slice(1);
    }
    return L('gast','guest');
  }, [userFirstName, userEmail, i18n.language]);

  // Customer sessions portal URL via backend redirect to environment-specific CLIENT_URL
  // Append token/user as fallback when 3rd-party cookies block SSO bridge
  const sessionsUrl = useMemo(() => {
    try {
      const apiBase = (apiUrl || '').replace(/\/+$/,'');
      const base = `${apiBase}/api/widget/portal/sessions`;
      const params = new URLSearchParams();
      params.set('sso', '1');
      // Ensure portal opens in same language as wizard
      const lang = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
      params.set('lang', lang);
      // Prefer token stored by widget login
      let token = '';
      try { token = sessionStorage.getItem('fit_user_token') || ''; } catch {}
      if (token) params.set('t', token);
      // Minimal user payload
      try {
        const raw = sessionStorage.getItem('fit_user');
        if (raw) {
          const u = JSON.parse(raw);
          const minimal = { id: (u?.id || u?.userId || u?.user_id || null), email: (u?.email || u?.emailAddress || null) };
          const packed = btoa(unescape(encodeURIComponent(JSON.stringify(minimal))));
          params.set('u', packed);
        } else if (userId || userEmail) {
          const minimal = { id: userId, email: userEmail };
          const packed = btoa(unescape(encodeURIComponent(JSON.stringify(minimal))));
          params.set('u', packed);
        }
      } catch {}
      return `${base}?${params.toString()}`;
    } catch {
      return `${(apiUrl||'').replace(/\/+$/,'')}/api/widget/portal/sessions?sso=1`;
    }
  }, [userId, userEmail, i18n.language, apiUrl]);

  const homepageUrl = useMemo(() => {
    try {
      const base = 'https://fit.brendr.io/';
      const params = new URLSearchParams();
      const lang = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
      params.set('lang', lang);
      return `${base}?${params.toString()}`;
    } catch {
      return 'https://fit.brendr.io/';
    }
  }, [i18n.language]);

  // Per-item image navigation
  const nextProductImage = (idx: number) => {
    flushSync(() => {
      setItems(prev => prev.map((p, i) => {
        if (i !== idx) return p;
        const imgs = Array.isArray(p.imageUrls) ? p.imageUrls : (p.imageUrl ? [p.imageUrl] : []);
        if (!imgs.length) return p;
        const cur = typeof p.currentImageIndex === 'number' ? p.currentImageIndex : 0;
        const next = (cur + 1) % imgs.length;
        if (i === 0) {
          try { lastSelectedImageUrlRef.current = String(imgs[next] || p.imageUrl || ''); } catch {}
        }
        return { ...p, currentImageIndex: next };
      }));
    });
  };
  const prevProductImage = (idx: number) => {
    flushSync(() => {
      setItems(prev => prev.map((p, i) => {
        if (i !== idx) return p;
        const imgs = Array.isArray(p.imageUrls) ? p.imageUrls : (p.imageUrl ? [p.imageUrl] : []);
        if (!imgs.length) return p;
        const cur = typeof p.currentImageIndex === 'number' ? p.currentImageIndex : 0;
        const prevIdx = cur === 0 ? imgs.length - 1 : cur - 1;
        if (i === 0) {
          try { lastSelectedImageUrlRef.current = String(imgs[prevIdx] || p.imageUrl || ''); } catch {}
        }
        return { ...p, currentImageIndex: prevIdx };
      }));
    });
  };
  const onRemoveItem = (idx: number) => {
    // Retailer widget: protect the initial item (slot 1) from removal
    if (!isPortal && idx === 0) return;
    setItems(prev => {
      const list = prev.filter((_, i) => i !== idx);
      try {
        const removed = prev[idx];
        const u = removed?.url ? String(removed.url) : '';
        if (u) {
          // Inform parent (retailer page) to update its persisted items
          window.parent.postMessage({ type: 'fit-items-remove', urls: [u] }, '*');
        }
      } catch {}
      return list;
    });
  };

  // Generate or retrieve a stable device id for guests (prefer parent-provided id)
  function getDeviceId(): string {
    // 1) Prefer deviceId sent by parent (retailer domain) via postMessage
    try {
      const fromParent = sessionStorage.getItem('fit_parent_device_id');
      if (fromParent) return fromParent;
    } catch {}
    // 2) Fallback to iframe-domain localStorage (may reset more often on some browsers)
    try {
      const key = 'fit_device_id';
      let id = localStorage.getItem(key);
      if (!id) {
        id = 'dev_' + Date.now().toString(36) + Math.random().toString(36).slice(2);
        localStorage.setItem(key, id);
      }
      return id;
    } catch {}
    // 3) Last resort: ephemeral id
    return 'dev_' + Date.now().toString(36);
  }

  // On mount: fetch session/profile and guest quota, load pasPhotos for logged-in users, fetch category-config
  useEffect(() => {
    let cancelled = false;
    let loggedInProfile = false;
    let lastKnownUserId: string | null = null;
    (async () => {
      try {
        // Skip profile fetch if user explicitly selected guest to avoid 401 noise
        const isGuest = (() => { try { return sessionStorage.getItem('fit_guest_selected') === 'true'; } catch { return false; } })();
        const persistedLoggedIn = (() => { try { return sessionStorage.getItem('fit_logged_in') === 'true'; } catch { return false; } })();
        if (!isGuest || persistedLoggedIn) {
          // Logged-in profile (to auto-fill pasPhoto_front)
          // Include Authorization header fallback for mobile browsers blocking third‑party cookies
          const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const r = await fetch(`${apiUrl}/api/consumer/profile`, { credentials: 'include', headers });
          const j = await r.json().catch(() => ({}));
          if (!cancelled && j && j.success) {
            loggedInProfile = true;
            lastKnownUserId = j.profile?.id || null;
            setUserId(j.profile?.id || null);
            setUserEmail(j.profile?.email || null);
            try {
              const prof = j.profile || {};
              const fn = (prof.first_name || prof.firstName || prof.firstname || (prof.name ? String(prof.name).split(' ')[0] : '')) || '';
              if (fn) setUserFirstName(String(fn));
              try {
                const accAt = (prof as any)?.acceptedTermsAt || null;
                const tver = (prof as any)?.termsVersion || null;
                if (accAt && tver === CURRENT_TERMS_VERSION) setConsentGiven(true);
              } catch {}
            } catch {}
            const pm: Record<string, string> = {};
            const prof = j.profile || {};
            const read = (k: string) => (prof[k] ?? prof[k.toLowerCase()] ?? null);
            const pairs: Array<[string, any]> = [
              ['pasphoto_front', read('pasPhoto_front')],
              ['pasphoto_side', read('pasPhoto_side')],
              ['pasphoto_fullbody_front', read('pasPhoto_fullBody_front') || read('pasPhoto_fullbody_front')],
              ['pasphoto_fullbody_side', read('pasPhoto_fullBody_side') || read('pasPhoto_fullbody_side')],
              ['pasphoto_spouse', read('pasPhoto_spouse')],
              ['pasphoto_member1', read('pasPhoto_member1')],
              ['pasphoto_member2', read('pasPhoto_member2')],
              ['pasphoto_member3', read('pasPhoto_member3')],
              ['pasphoto_member4', read('pasPhoto_member4')],
              ['pasphoto_room_1', read('pasPhoto_room_1')],
            ];
            for (const [id, v] of pairs) if (v) pm[id] = String(v);
            if (Object.keys(pm).length) setPhotoMap(pm);
            const pf = read('pasPhoto_front');
            if (pf && !photoUrl) setPhotoUrl(String(pf));
            // Refresh quota immediately for this logged-in user
            try { await refreshQuotaFor(j.profile?.id || null); } catch {}
            // Mark session as logged-in so later guest fetches don't override UI
            try { sessionStorage.setItem('fit_logged_in', 'true'); } catch {}
            // Persist lightweight user for downstream reads in this effect
            try { sessionStorage.setItem('fit_user', JSON.stringify({ id: j.profile?.id, email: j.profile?.email, firstName: (j.profile?.first_name || j.profile?.firstName || '') })); } catch {}
          }
        }
      } catch {}
      try {
        // Guest quota (no credentials; widget endpoints are wildcard CORS)
        if (loggedInProfile) {
          // Skip guest bootstrap when we already have a logged-in profile
          throw new Error('skip-guest-bootstrap');
        }
        const deviceId = getDeviceId();
        const body = { deviceId, shopId: context?.shopId || null };
        const gr = await fetch(`${apiUrl}/api/widget/guest/init`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        });
        const gj = await gr.json().catch(() => ({}));
        if (!cancelled && gj && gj.success) {
          // Only apply guest quota when actually in guest mode
          const isGuestNow = (() => { try { return sessionStorage.getItem('fit_guest_selected') === 'true' && sessionStorage.getItem('fit_logged_in') !== 'true'; } catch { return true; } })();
          if (isGuestNow && !userEmail && !userId) {
            setQuota({ remaining: gj.remaining, maxSessions: gj.maxSessions ?? null });
            if (!userId && gj.userId) { setUserId(String(gj.userId)); }
            // Persist guest JWT so we can attach Authorization header on uploads/session-start
            try {
              if (gj.token) {
                sessionStorage.setItem('fit_user_token', String(gj.token));
                sessionStorage.setItem('fit_guest_token', String(gj.token));
              }
            } catch {}
            try {
              const key = `fit_terms_accepted_${CURRENT_TERMS_VERSION}`;
              const v = sessionStorage.getItem(key);
              if (v === 'true') setConsentGiven(true);
            } catch {}
          }
        }
      } catch {}
      try {
        // Category config for photo slots and styles
        const q = new URLSearchParams();
        if (context?.shopId) q.set('shopId', String(context.shopId));
        else if (category) q.set('category', String(category));
        const langPref = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
        q.set('lang', langPref);
        const rr = await fetch(`${apiUrl}/api/widget/category-config?${q.toString()}`);
        const cj = await rr.json().catch(() => ({}));
        const s = cj?.settings || {};
        // Persist limits for item cap
        try {
          const lim = (s?.limits || {}) as any;
          setLimits({
            maxItemsGuest: (lim?.maxItemsGuest ?? lim?.max_items_guest ?? 0),
            maxItemsRegistered: (lim?.maxItemsRegistered ?? lim?.max_items_registered ?? 3),
            guestCanChooseStyle: !!(lim?.guestCanChooseStyle ?? lim?.guest_can_choose_style)
          });
        } catch {}
        const slots = Array.isArray(s.photoSlots)
          ? s.photoSlots.map((x: any) => String(x?.id || x).trim()).filter(Boolean).map(v => v.toLowerCase())
          : [];
        // Merge with n8n.defaultPhotoOrder to ensure required pasphotos are visible
        const n8nOrderRaw: string[] = Array.isArray(s?.n8n?.defaultPhotoOrder) ? s.n8n.defaultPhotoOrder : [];
        const orderIds = n8nOrderRaw.map((v: any) => {
          const id = String(v || '').trim();
          const withPrefix = id.startsWith('pasphoto_') ? id : (id ? `pasphoto_${id}` : '');
          return withPrefix.toLowerCase();
        }).filter(Boolean);
        setDefaultPhotoOrder(orderIds);
        const mergedSlots = Array.from(new Set([ ...orderIds, ...slots ]));
        if (!cancelled) {
          if (mergedSlots.length) setPhotoSlots(mergedSlots);
          // Determine required slots: explicit required flags, otherwise first of default order
          try {
            let req: string[] = [];
            if (Array.isArray(s.photoSlots)) {
              req = s.photoSlots
                .filter((sl: any) => sl && (sl.required === true || sl.required === 'true'))
                .map((sl: any) => String(sl.id || '').toLowerCase())
                .map((id: string) => (id.startsWith('pasphoto_') ? id : `pasphoto_${id}`));
            }
            if (!req.length && orderIds.length) req = [orderIds[0]];
            setRequiredSlots(Array.from(new Set(req.filter(Boolean))));
          } catch {}
          const styles = Array.isArray(s.styles) ? s.styles : [];
          const opts: StyleOption[] = styles.map((it: any) => {
            let name: string = '';
            try {
              if (typeof it?.label === 'string') name = it.label;
              else if (it?.label && typeof it.label === 'object') {
                const langPref = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
                name = it.label[langPref] || it.label[langPref === 'nl' ? 'en' : 'nl'] || '';
              }
            } catch {}
            if (!name) name = String(it?.name || (i18n.language?.startsWith('nl') ? `Stijl ${it?.id}` : `Style ${it?.id}`));
            // Prefer explicit owner-provided info over CSV enrichment (desc)
            const desc = (it as any)?.info ? String((it as any).info) : ((it as any)?.desc ? String((it as any).desc) : undefined);
            const icon = (it as any)?.icon ? String((it as any).icon) : undefined;
            const color = (it as any)?.color ? String((it as any).color) : undefined;
            return { id: String(it?.id ?? ''), name, desc, icon, color } as StyleOption;
          });
          if (opts.length) { setStyleOptions(opts); if (!style) setStyle(String(opts[0].id)); }
          // Build slot labels (prefer nl, fallback en, then id)
          const lbls: Record<string, string> = {};
          try {
            for (const sl of (s.photoSlots || [])) {
              const origId = String(sl?.id || '').trim();
              if (!origId) continue;
              const id = origId.toLowerCase();
              let lab = '';
              try {
                if (typeof sl?.label === 'string') lab = sl.label;
                else if (sl?.label && typeof sl.label === 'object') {
                  const langPref = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
                  lab = sl.label[langPref] || sl.label[langPref === 'nl' ? 'en' : 'nl'] || '';
                }
              } catch {}
              lab = lab || origId;
              lbls[id] = String(lab);
              lbls[origId] = String(lab);
            }
          } catch {}
          setSlotLabels(lbls);
          // Info/tip localized strings
          try {
            const pick = (val: any): string => {
              if (!val) return '';
              if (typeof val === 'string') return val;
              if (typeof val === 'object') {
                const langPref = (i18n.language && i18n.language.startsWith('nl')) ? 'nl' : 'en';
                return String(val[langPref] || val[langPref === 'nl' ? 'en' : 'nl'] || '');
              }
              return '';
            };
            const ih = pick(s.infoBoxHeader);
            const ib = pick(s.infoBoxBody);
            const th = pick(s.tipBoxHeader);
            const tb = pick(s.tipBoxBody);
            setInfoHeader(ih);
            setInfoBody(ib);
            setTipHeaderTitle(th);
            setTipBodyText(tb);
            if (!ib) {
              const legacy = pick(s.infoBox);
              if (legacy) setCategoryInfoBox(legacy);
            } else {
              setCategoryInfoBox(ib);
            }
          } catch {}
          // perStylePhotoOrder (style-specific desired order)
          try {
            const rawMap = (s?.n8n?.perStylePhotoOrder || s?.n8n?.per_style_photo_order) as any;
            if (rawMap && typeof rawMap === 'object') {
              const out: Record<string, string[]> = {};
              Object.keys(rawMap).forEach((k) => {
                const arr = Array.isArray(rawMap[k]) ? rawMap[k] : [];
                const norm = arr.map((x: any) => String(x || '').trim()).map((id: string) => (id.startsWith('pasphoto_') ? id : (id ? `pasphoto_${id}` : ''))).map((id: string) => id.toLowerCase()).filter(Boolean);
                if (norm.length) out[String(k)] = norm;
              });
              setPerStylePhotoOrder(out);
            }
          } catch {}
        }
      } catch {}
      try {
        // Quota for logged-in user (retailer-level) or guest fallback
        const deviceId = getDeviceId();
        const params = new URLSearchParams();
        if (context?.shopId) params.set('shopId', String(context.shopId));
        // Prefer freshly fetched profile id, then state, then persisted storage
        let uidForQuota: string | null = lastKnownUserId || userId;
        if (!uidForQuota) {
          try { const u = sessionStorage.getItem('fit_user'); if (u) { const parsed = JSON.parse(u); if (parsed?.id) uidForQuota = String(parsed.id); } } catch {}
        }
        const usedUser = !!uidForQuota;
        if (usedUser) params.set('userId', String(uidForQuota)); else params.set('deviceId', deviceId);
        const qr = await fetch(`${apiUrl}/api/widget/quota?${params.toString()}`, { credentials: 'include' });
        const qj = await qr.json().catch(() => ({}));
        if (!cancelled && qj && qj.success) {
          // Do not let a guest/device quota override a logged-in view within this boot cycle
          if (loggedInProfile && !usedUser) {
            // skip applying guest quota
          } else {
            setQuota({ remaining: qj.remaining ?? null, maxSessions: qj.maxSessions ?? null });
          }
        }
      } catch {}
    })();
    // Remove any initial inline loader the shell inserted
    try { const el = document.getElementById('fit-initial-loader'); if (el) el.remove(); } catch {}
    return () => { cancelled = true; };
  }, [apiUrl, context?.shopId, category, i18n.language]);

  // Listen for login updates and bootstrap from storage
  useEffect(() => {
    const onCustom = (e: any) => { try { updateFromUser(e?.detail); } catch {} };
    const onMessage = (e: MessageEvent) => {
      try {
        const d: any = e && e.data && typeof e.data === 'object' ? (e.data as any) : null;
        if (!d) return;
        if (d.type === 'fit-user-updated') {
          updateFromUser(d.user);
          // Ensure pasPhotos refresh immediately after login on mobile
          try { loadUserProfilePhotos(); } catch {}
        } else if (d.type === 'fit-auth-init') {
          // Parent initializes iframe with existing auth
          try { if (d.token) sessionStorage.setItem('fit_user_token', String(d.token)); } catch {}
          try { if (d.user) sessionStorage.setItem('fit_user', JSON.stringify(d.user)); } catch {}
          if (d.user) {
            updateFromUser(d.user);
          } else {
            // If only token was provided, still refresh quota as user may be recognized by cookie
            try { refreshQuotaFor(null); } catch {}
          }
          // Also mark as logged in for this session to prevent guest overrides
          try { if (d.token) sessionStorage.setItem('fit_logged_in', 'true'); } catch {}
        } else if (d.type === 'fit-items-init' && Array.isArray(d.items)) {
          // Items pushed from parent after clicking "+" on PDPs
          const incoming: any[] = d.items || [];
          setItems(prev => {
            const norm = (u?: string) => (u ? String(u).toLowerCase() : '');
            const existingUrls = new Set(prev.map(p => norm(p.url)));
            const mapped: WizardItem[] = incoming.map((it: any) => {
              const imgRaw = (it && (it.imageUrl || it.image)) || '';
              const img = typeof imgRaw === 'object' ? (imgRaw?.url || imgRaw?.src || imgRaw?.contentUrl || '') : String(imgRaw || '');
              const imgsArr = Array.isArray(it?.images)
                ? it.images.map((v: any) => (typeof v === 'object' ? (v?.url || v?.src || v?.contentUrl || '') : String(v))).filter(Boolean)
                : (img ? [img] : []);
              return {
                title: String(it?.title || 'Item'),
                price: it?.price ? String(it.price) : undefined,
                imageUrl: imgsArr[0] || img || '',
                imageUrls: imgsArr,
                currentImageIndex: 0,
                url: it?.url ? String(it.url) : undefined,
              } as WizardItem;
            }).filter(it => it.imageUrl && it.url && !existingUrls.has(norm(it.url)));

            // Keep slot 0 intact; append new items after existing ones, cap at 4 total
            const head = prev.length ? [prev[0]] : [];
            const tail = prev.length > 1 ? prev.slice(1) : [];
            const merged = [...head, ...tail, ...mapped];
            return merged.slice(0, itemsCap);
          });
        } else if (d.type === 'fit-items-append' && Array.isArray(d.items)) {
          // Live append from parent when modal is already open
          const incoming: any[] = d.items || [];
          setItems(prev => {
            const norm = (u?: string) => (u ? String(u).toLowerCase() : '');
            const existingUrls = new Set(prev.map(p => norm(p.url)));
            const mapped: WizardItem[] = incoming.map((it: any) => {
              const imgRaw = (it && (it.imageUrl || it.image)) || '';
              const img = typeof imgRaw === 'object' ? (imgRaw?.url || imgRaw?.src || imgRaw?.contentUrl || '') : String(imgRaw || '');
              const imgsArr = Array.isArray(it?.images)
                ? it.images.map((v: any) => (typeof v === 'object' ? (v?.url || v?.src || v?.contentUrl || '') : String(v))).filter(Boolean)
                : (img ? [img] : []);
              return {
                title: String(it?.title || 'Item'),
                price: it?.price ? String(it.price) : undefined,
                imageUrl: imgsArr[0] || img || '',
                imageUrls: imgsArr,
                currentImageIndex: 0,
                url: it?.url ? String(it.url) : undefined,
              } as WizardItem;
            }).filter(it => it.imageUrl && it.url && !existingUrls.has(norm(it.url)));

            const head = prev.length ? [prev[0]] : [];
            const tail = prev.length > 1 ? prev.slice(1) : [];
            const merged = [...head, ...tail, ...mapped];
            return merged.slice(0, itemsCap);
          });
        } else if (d.type === 'fit-device-id-init' && d.deviceId) {
          // Persist parent-provided device id for stable guest identity
          try { sessionStorage.setItem('fit_parent_device_id', String(d.deviceId)); } catch {}
        }
      } catch {}
    };
    window.addEventListener('fit-user-updated' as any, onCustom as any);
    window.addEventListener('message', onMessage);
    // Bootstrap from persisted session
    try {
      const u = sessionStorage.getItem('fit_user');
      if (u) updateFromUser(JSON.parse(u));
    } catch {}
    return () => {
      window.removeEventListener('fit-user-updated' as any, onCustom as any);
      window.removeEventListener('message', onMessage);
    };
  }, [apiUrl, context?.shopId]);

  // Lottie loader (submission)
  useEffect(() => {
    if (!isSubmitting) return;
    if (!lottieRef.current || !(window as any).lottie) return;
    try {
      const anim = (window as any).lottie.loadAnimation({
        container: lottieRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: 'https://hruleghaabwolyrkzzoc.supabase.co/storage/v1/object/public/logos/Loading%20Bubbles.json'
      });
      return () => { try { anim.destroy(); } catch {} };
    } catch {}
  }, [isSubmitting]);

  const onAddUrl = async () => {
    const url = newUrl.trim();
    if (!url) return;
    setIsScraping(true);
    try {
      const rr = await fetch(`${apiUrl}/api/widget/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const out = await rr.json().catch(() => ({}));
      if (out && out.success && out.data) {
        const d = out.data as any;
        const title = String(d.title || 'Item');
        const price = d.price ? String(d.price) : undefined;
        const img = (d.image && typeof d.image === 'object') ? (d.image.url || d.image.src || '') : (d.image || '');
        const imgs = Array.isArray(d.images) ? d.images.filter(Boolean).map((v:any)=> typeof v === 'object' ? (v?.url || v?.src || v?.contentUrl || '') : String(v)).filter((s:string)=>!!s) : [];
        const all = [img, ...imgs].filter(Boolean);
        setItems((prev) => prev.length >= itemsCap ? prev : [...prev, { title, price, imageUrl: all[0] || img || '', imageUrls: all, currentImageIndex: 0, url }]);
      } else {
        setItems((prev) => prev.length >= itemsCap ? prev : [...prev, { title: L('Toegevoegd item','Added item'), url }]);
      }
      setNewUrl('');
      setShowAdd(false);
    } catch {
      setItems((prev) => prev.length >= itemsCap ? prev : [...prev, { title: 'Toegevoegd item', url }]);
    } finally {
      setIsScraping(false);
    }
  };

  const onResetItems = () => setItems([]);

  const onUploadClick = (slotId?: string) => { if (slotId) { currentUploadSlotRef.current = slotId; flushSync(() => setUploadSlot(slotId)); } try { if (fileInputRef.current) (fileInputRef.current as HTMLInputElement).value = ''; } catch {} fileInputRef.current?.click(); };
  const guardedUploadClick = (slotId?: string) => {
    if (!consentGiven) { setShowTip(L('Je moet akkoord gaan met de voorwaarden en het privacybeleid','You must agree to the terms and privacy policy')); setTimeout(()=>setShowTip(null), 1800); return; }
    if (slotId) { currentUploadSlotRef.current = slotId; flushSync(() => setUploadSlot(slotId)); }
    // debounce/guard: if a dialog is already opening/open, skip
    if (fileDialogGuardRef.current) return;
    fileDialogGuardRef.current = true;
    try { if (fileInputRef.current) (fileInputRef.current as HTMLInputElement).value = ''; } catch {}
    fileInputRef.current?.click();
    // fallback reset in case user cancels and no change event fires
    try {
      if (fileDialogGuardTimerRef.current) window.clearTimeout(fileDialogGuardTimerRef.current);
      fileDialogGuardTimerRef.current = window.setTimeout(() => { fileDialogGuardRef.current = false; }, 2500);
    } catch {}
  };
  const onConsentChange = async (checked: boolean) => {
    const isGuest = !userEmail;
    if (checked) {
      if (isGuest) {
        try {
          const deviceId = getDeviceId();
          await fetch(`${apiUrl}/api/widget/guest/accept-terms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId, termsVersion: CURRENT_TERMS_VERSION, shopId: context?.shopId || null })
          });
        } catch {}
        try { sessionStorage.setItem(`fit_terms_accepted_${CURRENT_TERMS_VERSION}`, 'true'); } catch {}
        setConsentGiven(true);
        return;
      }
      try {
        const payload: any = { accepted_terms_at: new Date().toISOString(), terms_version: CURRENT_TERMS_VERSION };
        const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const r = await fetch(`${apiUrl}/api/consumer/profile`, { method: 'PUT', credentials: 'include', headers, body: JSON.stringify(payload) });
        if (r.ok) setConsentGiven(true);
      } catch {}
      return;
    }
    // Uncheck path: remove any uploaded photos and clear consent flag
    try {
      const entries = Object.entries(photoMap).filter(([_, v]) => !!v);
      if (entries.length) {
        if (isGuest) {
          const deviceId = getDeviceId();
          const shopIdParam = context?.shopId ? `&shopId=${encodeURIComponent(String(context.shopId))}` : '';
          await Promise.all(entries.map(async ([slotId]) => {
            const type = slotIdToType(slotId);
            const url = `${apiUrl}/api/widget/guest/pasphoto/${encodeURIComponent(type)}?deviceId=${encodeURIComponent(deviceId)}${shopIdParam}`;
            try { await fetch(url, { method: 'DELETE' }); } catch {}
          }));
        } else {
          const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          await Promise.all(entries.map(async ([slotId]) => {
            const type = slotIdToType(slotId);
            try { await fetch(`${apiUrl}/api/consumer/profile/pasphoto/${encodeURIComponent(type)}`, { method: 'DELETE', credentials: 'include', headers }); } catch {}
          }));
        }
      }
    } catch {}
    try { setPhotoMap({}); setPhotoUrl(''); setUploadSlot(null); } catch {}
    try { if (isGuest) sessionStorage.removeItem(`fit_terms_accepted_${CURRENT_TERMS_VERSION}`); } catch {}
    setConsentGiven(false);
  };
  async function onDeleteSlot(slotId: string) {
    try {
      const type = slotIdToType(slotId);
      if (!type) return;
      const isGuest = !userEmail;
      if (isGuest) {
        try {
          const deviceId = getDeviceId();
          const shopIdParam = context?.shopId ? `&shopId=${encodeURIComponent(String(context.shopId))}` : '';
          const url = `${apiUrl}/api/widget/guest/pasphoto/${encodeURIComponent(type)}?deviceId=${encodeURIComponent(deviceId)}${shopIdParam}`;
          await fetch(url, { method: 'DELETE' });
        } catch {}
      } else {
        try {
          const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          await fetch(`${apiUrl}/api/consumer/profile/pasphoto/${encodeURIComponent(type)}`, { method: 'DELETE', credentials: 'include', headers });
        } catch {}
      }
    } finally {
      try {
        setPhotoMap(prev => {
          const next = { ...prev } as Record<string, string>;
          try { delete (next as any)[slotId]; } catch {}
          return next as any;
        });
        if (slotId === 'pasphoto_front') setPhotoUrl('');
        setUploadSlot(null);
        currentUploadSlotRef.current = null;
      } catch {}
      try { setShowTip(L('Foto verwijderd','Photo removed')); setTimeout(() => setShowTip(null), 1200); } catch {}
    }
  }
  async function handleFileFromInput(inputEl: HTMLInputElement) {
    const file = inputEl.files?.[0];
    if (!file) return;
    let slot = uploadSlot || currentUploadSlotRef.current || (requiredSlots[0] || 'pasphoto_front');
    try {
      if (slot) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const url = String(reader.result || '');
            setPhotoMap(prev => ({ ...prev, [slot]: url }));
            if (slot === 'pasphoto_front') setPhotoUrl(url);
          } catch {}
        };
        reader.readAsDataURL(file);
      }
    } catch {}
    if (userEmail && slot) {
      try {
        const type = slotIdToType(slot);
        const fd = new FormData();
        fd.append('pasPhoto', file);
        const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(`${apiUrl}/api/consumer/profile/pasphoto/${encodeURIComponent(type)}`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: fd
        });
        const jj = await resp.json().catch(()=>({}));
        if (resp.ok && jj && jj.photoUrl) {
          setPhotoMap(prev => ({ ...prev, [slot]: String(jj.photoUrl) }));
          setUploadSlot(null);
          currentUploadSlotRef.current = null;
          try { inputEl.value = ''; } catch {}
          return;
        }
      } catch {}
    }
    if (!userEmail && slot) {
      try {
        const type = slotIdToType(slot);
        const fd = new FormData();
        fd.append('pasPhoto', file);
        const deviceId = getDeviceId();
        const shopIdParam = context?.shopId ? `&shopId=${encodeURIComponent(String(context.shopId))}` : '';
        const url = `${apiUrl}/api/widget/guest/pasphoto/${encodeURIComponent(type)}?deviceId=${encodeURIComponent(deviceId)}${shopIdParam}`;
        const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(url, { method: 'POST', headers, body: fd });
        const jj = await resp.json().catch(()=>({}));
        if (resp.ok && jj && jj.photoUrl) {
          setPhotoMap(prev => ({ ...prev, [slot]: String(jj.photoUrl) }));
          setPhotoUrl(String(jj.photoUrl));
          setUploadSlot(null);
          currentUploadSlotRef.current = null;
          try { inputEl.value = ''; } catch {}
          return;
        }
      } catch {}
    }
    try { setUploadSlot(null); currentUploadSlotRef.current = null; } catch {}
    try { inputEl.value = ''; } catch {}
  };
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // clear guard as soon as we get a selection/cancel event
    try { if (fileDialogGuardTimerRef.current) window.clearTimeout(fileDialogGuardTimerRef.current); } catch {}
    fileDialogGuardRef.current = false;
    await handleFileFromInput(e.currentTarget as HTMLInputElement);
  };

  const onStartSession = async () => {
    try {
      const deviceId = getDeviceId();
      const payload: any = {
        userId: userId || null,
        deviceId,
        retailerId: context?.retailerId || null,
        shopId: context?.shopId || null,
        language: ((i18n.language && String(i18n.language).toLowerCase().split('-')[0]) || 'nl'),
        userPhotos: (() => {
          const slots = (photoSlots && photoSlots.length)
            ? photoSlots
            : ['pasphoto_front','pasphoto_side','pasphoto_fullbody_front','pasphoto_fullbody_side'];
          const fromMap = slots
            .map(s => photoMap[s])
            .filter(Boolean)
            .map(String)
            .filter(u => !/^data:/i.test(u));
          const fallback = (photoUrl && !/^data:/i.test(photoUrl)) ? [photoUrl] : [];
          return fromMap.length ? fromMap : fallback;
        })(),
        category: category || null,
        style: style || null,
        // Backwards-compatible array used by current backend flow
        products: items.map(it => {
          const arrRaw = Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : []);
          const cur = typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
          const idx = Math.min(Math.max(cur, 0), Math.max(arrRaw.length - 1, 0));
          const primary = arrRaw[idx] || it.imageUrl || '';
          if (it.isUploaded) {
            return {
              name: 'not available',
              url: it.url,
              price: 'not available',
              imageUrl: primary,
              imageUrls: [] as string[]
            };
          }
          return {
            name: it.title || 'Product',
            url: it.url,
            price: it.price,
            imageUrl: primary,
            imageUrls: arrRaw
          };
        }),
        // New explicit structure so n8n can consume all item slots deterministically
        itemSlots: items.map((it, slotIndex) => {
          const arr = Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : []);
          const currentImageIndex = typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
          const safeIndex = Math.min(Math.max(currentImageIndex, 0), Math.max(arr.length - 1, 0));
          const primaryImageUrl = arr[safeIndex] || it.imageUrl || '';
          return {
            slotIndex,
            title: it.title || 'Item',
            url: it.url || null,
            price: it.price || null,
            primaryImageUrl,
            currentImageIndex: safeIndex,
            imageUrls: arr
          };
        }),
        itemsCount: items.length
      };
      // Reset feedback-related state before starting a new session
      setFeedback('');
      setFeedbackInput('');
      setSatisfied(null);
      setShowFeedbackModal(false);
      setShowFeedbackInfo(false);
      setFavorite(false);
      setResultImageLoaded(false);
      setIsSubmitting(true);
      setResultUrl('');
      setSessionId(null);
      const token = (() => { try { return sessionStorage.getItem('fit_user_token') || ''; } catch { return ''; } })();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const r = await fetch(`${apiUrl}/api/widget/n8n/tryon`, {
        method: 'POST', headers, body: JSON.stringify(payload)
      });
      const j = await r.json().catch(() => ({}));
      try {
        const n8nParsed: any = Array.isArray(j) ? (j[0] || j) : j;
        const n8nResp: any = (n8nParsed && typeof n8nParsed === 'object') ? (n8nParsed.response || n8nParsed) : null;
        const n8nCode = String(n8nResp?.code || '');
        const n8nError = String(n8nResp?.error || '');
        if (n8nResp && n8nResp.success === false && (n8nCode === 'INVALID_INPUT' || /invalid\s+input/i.test(n8nError))) {
          setIsSubmitting(false);
          setShowTip(L('Eén van je pasfoto’s voldoet niet aan de vereisten.\nVervang de foto en probeer het opnieuw.','One of your photos doesn’t meet the requirements.\nPlease upload a new one and try again.'));
          setTimeout(() => setShowTip(null), 3500);
          return;
        }
      } catch {}
      if (!r.ok) {
        setIsSubmitting(false);
        console.warn('FiT sessie mislukt', j);
        if (r.status === 429 || (j && (j.limitReached || j.remaining === 0))) {
          const isLoggedInNow = (() => { try { return (sessionStorage.getItem('fit_logged_in') === 'true') || !!userEmail || !!userId; } catch { return !!userEmail || !!userId; } })();
          const msg = isLoggedInNow
            ? L('Je hebt vandaag al je FiT sessies gebruikt. Morgen ontvang je weer nieuw tegoed', 'You have used all your FiT sessions for today. New credits will be available tomorrow')
            : L('Je hebt geen FiT sessie meer tegoed. Log in om extra sessies te kunnen doen','You have no FiT sessions left. Log in for more sessions');
          setShowTip(msg);
          setTimeout(() => setShowTip(null), 2500);
        }
        return;
      }
      const resp = Array.isArray(j) ? (j[0] || {}) : (j || {});
      // Pick image URL based on branding preference from retailer/shop:
      // - brandingHideLogo = true  => prefer resultImageUrl (no branding)
      // - brandingHideLogo = false => prefer displayImageUrl (with branding)
      const preferUrl: string = (
        !!brandingHideLogo
          ? (resp.resultImageUrl || resp.displayImageUrl)
          : (resp.displayImageUrl || resp.resultImageUrl)
      ) || '';
      let finalUrl = preferUrl;
      if (!finalUrl) {
        // Fallback to legacy builder if API didn't return explicit URLs
        const baseUrl: string = resp.displayImageUrl || resp.resultImageUrl || '';
        finalUrl = buildDisplayUrlByBranding(baseUrl, !!brandingHideLogo) || baseUrl || '';
      }
      setSessionId(resp.sessionId || null);
      setResultUrl(finalUrl);
      if (resp && (resp.remaining !== undefined || resp.maxSessions !== undefined)) {
        setQuota({ remaining: resp.remaining, maxSessions: resp.maxSessions });
      }
      setIsSubmitting(false);
    } catch (e) {
      console.error('Start session error', e);
      setIsSubmitting(false);
    }
  };

  // Build display/download URLs like legacy
  const CLOUDINARY_FETCH_BASE = 'https://res.cloudinary.com/dbzm9mwun/image/fetch';
  const CLOUDINARY_TRANSFORM_DISPLAY = 'f_auto,q_auto/l_BrendR_w64uef,g_north_east,w_280,o_100,x_680,y_700';
  const CLOUDINARY_TRANSFORM_DOWNLOAD = 'f_auto,q_auto,fl_attachment/l_BrendR_w64uef,g_north_east,w_280,o_100,x_680,y_700';
  const CLOUDINARY_TRANSFORM_DOWNLOAD_NO_BRAND = 'f_auto,q_auto,fl_attachment';

  const VITE_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const VITE_SUPABASE_ASSETS_BASE = import.meta.env.VITE_SUPABASE_ASSETS_BASE as string | undefined;
  const __WIN_ASSETS_BASE = (typeof window !== 'undefined' && (window as any).__ASSETS_BASE) as string | undefined;
  const ASSETS_BASE = (((__WIN_ASSETS_BASE && __WIN_ASSETS_BASE.trim())
    || (VITE_SUPABASE_ASSETS_BASE && VITE_SUPABASE_ASSETS_BASE.trim())
    || (VITE_SUPABASE_URL ? `${VITE_SUPABASE_URL.replace(/\/+$/, '')}/storage/v1/object/public` : '')
  ) || '').replace(/\/+$/, '');

  function toAbsolutePublicUrl(pathOrUrl?: string): string {
    if (!pathOrUrl) return '';
    const s = String(pathOrUrl);
    const lower = s.toLowerCase();
    if (lower.startsWith('http://') || lower.startsWith('https://')) return s;
    const base = ASSETS_BASE;
    let clean = s;
    while (clean.startsWith('/')) clean = clean.slice(1);
    return base + '/' + clean;
  }
  function ensureDownloadTransformFromCloudinary(url: string): string {
    try {
      const prefix = CLOUDINARY_FETCH_BASE + '/';
      if (!url.startsWith(prefix)) return url;
      const rest = url.slice(prefix.length);
      const lastSlash = rest.lastIndexOf('/');
      if (lastSlash < 0) return url;
      const transform = rest.slice(0, lastSlash);
      const encoded = rest.slice(lastSlash + 1);
      const parts = transform.split('/');
      let first = parts[0] || '';
      if (!first.includes('fl_attachment')) {
        first = first ? `${first},fl_attachment` : 'fl_attachment';
        parts[0] = first;
      }
      return prefix + parts.join('/') + '/' + encoded;
    } catch {
      return url;
    }
  }
  function buildDownloadUrl(src?: string): string {
    if (!src) return '';
    if (src.startsWith(CLOUDINARY_FETCH_BASE + '/')) return ensureDownloadTransformFromCloudinary(src);
    const source = toAbsolutePublicUrl(src);
    const encoded = encodeURIComponent(source);
    const transform = brandingHideLogo ? CLOUDINARY_TRANSFORM_DOWNLOAD_NO_BRAND : CLOUDINARY_TRANSFORM_DOWNLOAD;
    return `${CLOUDINARY_FETCH_BASE}/${transform}/${encoded}`;
  }
  function buildDisplayUrlByBranding(src?: string, hide?: boolean): string {
    if (!src) return '';
    if (src.startsWith(CLOUDINARY_FETCH_BASE + '/')) return src;
    const source = toAbsolutePublicUrl(src);
    if (hide) return source;
    const encoded = encodeURIComponent(source);
    return `${CLOUDINARY_FETCH_BASE}/${CLOUDINARY_TRANSFORM_DISPLAY}/${encoded}`;
  }

  function onDownload() {
    if (!resultUrl) return;
    const url = buildDownloadUrl(resultUrl);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fit_result.jpg';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  async function onShare() {
    if (!resultUrl) return;
    const text = L('Hé, kijk mij eens! Wat denk jij, is dit een fit?','Hey, check me out! What do you think, is this a fit?');
    // Directe Web Share (bijlage waar mogelijk), geen relay
    try {
      if ((navigator as any).share) {
        try {
          const resp = await fetch(resultUrl);
          const blob = await resp.blob();
          const file = typeof File !== 'undefined' ? new File([blob], 'fit_result.jpg', { type: blob.type || 'image/jpeg' }) : null;
          if (file && (navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
            await (navigator as any).share({ files: [file], title: 'Mijn FiT Resultaat', text });
            return;
          }
          await (navigator as any).share({ title: 'Mijn FiT Resultaat', text, url: resultUrl });
          return;
        } catch {}
      }
    } catch {}

    // Fallback: kopieer link naar klembord
    try {
      await navigator.clipboard.writeText(resultUrl);
      setShowTip(L('Afbeeldingslink gekopieerd','Image link copied'));
      setTimeout(() => setShowTip(null), 1500);
      return;
    } catch {}

    // Laatste redmiddel: toon melding (geen nieuwe tab)
    setShowTip(L('Delen niet ondersteund op dit apparaat/browser','Sharing not supported on this device/browser'));
    setTimeout(() => setShowTip(null), 1500);
  }

  function onFullscreen() { setShowFullscreen(true); }
  function onCloseFullscreen() { setShowFullscreen(false); }

  async function submitFeedbackApi(_satisfied: boolean, text: string) {
    try {
      setSatisfied(_satisfied);
      setFeedback(text);
      if (!sessionId) return; // need session id from try-on
      const token = sessionStorage.getItem('fit_user_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const payload = { satisfied: !!_satisfied, feedback: String(text || '').slice(0, 1000) };
      // Prefer consumer route (supports cookie-based auth)
      let ok = false;
      try {
        const r1 = await fetch(`${apiUrl}/api/consumer/fit-sessions/${encodeURIComponent(sessionId)}/feedback`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        ok = r1.ok;
      } catch {}
      if (!ok) {
        // Fallback to legacy sessions route (header token)
        await fetch(`${apiUrl}/api/sessions/feedback`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ sessionId, ...payload })
        });
      }
    } catch {}
  }

  async function onSubmitNegativeFeedback() {
    const text = (feedbackInput || '').trim();
    if (!text) {
      setShowTip(L('Geef a.u.b. een korte toelichting','Please provide a short explanation'));
      setTimeout(() => setShowTip(null), 1600);
      return;
    }
    await submitFeedbackApi(false, text);
    setShowFeedbackModal(false);
    setShowFeedbackInfo(false);
    setShowTip(L('Bedankt voor je feedback','Thanks for your feedback'));
    setTimeout(() => setShowTip(null), 1500);
  }

  return (
    <div className="w-full h-screen bg-transparent flex">
      <div className="w-full h-full overflow-hidden bg-transparent rounded-2xl flex flex-col min-h-0">
        {/* Header */}
        <div
          className="px-0 py-6 relative"
          style={{
            background: (() => {
              const base = String(retailerData?.primaryColor || '#f97316');
              const hex = /^#([0-9a-fA-F]{6})$/.test(base) ? base : '#f97316';
              const r = parseInt(hex.slice(1,3),16);
              const g = parseInt(hex.slice(3,5),16);
              const b = parseInt(hex.slice(5,7),16);
              const dark = (v: number) => Math.max(0, Math.min(255, Math.floor(v * 0.82)));
              const toHex = (v: number) => v.toString(16).padStart(2,'0');
              const darker = `#${toHex(dark(r))}${toHex(dark(g))}${toHex(dark(b))}`;
              return `linear-gradient(90deg, ${hex}, ${darker})`;
            })()
          }}
        >
          <div className="px-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                {retailerData.logoUrl ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={`${ASSETS_BASE}/logos/FiT_combo_Retail_Logo.png`}
                      alt="FiT by"
                      className="h-12 md:h-14 w-auto"
                    />
                    <img
                      src={retailerData.logoUrl}
                      alt="Retailer logo"
                      className="h-10 md:h-12 w-auto object-contain bg-white/0 rounded"
                    />
                  </div>
                ) : (
                  <img src={`${ASSETS_BASE}/logos/main_site_black.svg`} alt="FiT" className="h-20 w-auto" />
                )}
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/30 relative group">
                <p className="text-white font-semibold text-sm flex items-center">
                  {L('Sessies', 'Sessions')}: <span className="font-bold ml-1">{sessionsUsed ?? '–'}/{quota?.maxSessions ?? '–'}</span>
                  {quota?.remaining != null && Number(quota.remaining) <= 0 && (
                    <span className="ml-2 inline-flex items-center justify-center" aria-label="Geen sessies meer">
                      <Info className="w-4 h-4 text-red-200" />
                    </span>
                  )}
                </p>
                {quota?.remaining != null && Number(quota.remaining) <= 0 && (
                  <div className="absolute right-0 top-full mt-2 hidden group-hover:block">
                    <div className="bg-white text-slate-700 text-xs font-medium px-3 py-2 rounded-lg shadow-lg border border-slate-200 whitespace-nowrap">
                      {Number(quota?.maxSessions ?? 3) !== 3
                        ? L('Je hebt al je FiT sessies voor vandaag gebruikt. Morgen ontvang je weer nieuw tegoed', 'You have used all your FiT sessions for today. New credits will be available tomorrow')
                        : L('Registreer je en log in voor meer FiT sessies.', 'Register and log in for more FiT sessions.')}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-white/90 text-sm">
              {L('Zie hoe items jou staan voordat je koopt! Je shopt momenteel bij', 'See how items look on you before you buy! You are shopping at')} {retailerData.shopName}
            </p>
          </div>
        </div>

        {/* User Info Bar */}
        <div className={`${resultUrl ? 'bg-transparent border-transparent' : 'bg-slate-50 border-slate-200'} border-b px-6 py-3`}>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-600">
                <UserCircle className="w-4 h-4" />
              </span>
              <span className="font-medium text-slate-900">{L('Hoi','Hi')} {displayFirstName}!</span>
            </p>
            <div className="flex items-center gap-3">
              {userEmail ? (
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors" onClick={onLogout}>
                  {L('Uitloggen','Log out')}
                </button>
              ) : (
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors" onClick={openLogin}>
                  {L('Inloggen','Log in')}
                </button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
        </div>

        {/* Tabs */}
        {!resultUrl && (
        <div className="hidden">
          <button 
            onClick={() => setStep(1)}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-all ${
              step === 1 
                ? 'text-white bg-orange-500 border-b-2 border-orange-500' 
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            1 Items
          </button>
          <button 
            onClick={async () => {
              if (step === 1) {
                const it = items[0];
                const imgs = it ? (Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : [])) : [];
                const cur = it && typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
                const imgSrc = imgs[cur] || it?.imageUrl || '';
                const hasAnyImage = imgs.length > 0 && String(imgSrc || '').trim().length > 0;
                if (!hasAnyImage) {
                  setShowTip('Dit product kan helaas niet mee in je FiT sessie. Probeer het met een ander product.');
                  setTimeout(() => setShowTip(null), 2500);
                  return;
                }
                const ok = await isImageLoadable(imgSrc);
                if (!ok) {
                  setShowTip('De productafbeelding kon niet worden geladen. Sluit dit venster en start FiT opnieuw vanaf een productpagina');
                  setTimeout(() => setShowTip(null), 10000);
                  return;
                }
              }
              setStep(2);
            }}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-all ${
              step === 2 
                ? 'text-white bg-orange-500 border-b-2 border-orange-500' 
                : step > 2 
                  ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  : 'text-slate-400'
            }`}
          >
            2 {L('Pasfoto','Photo')}
          </button>
          <button 
            onClick={async () => {
              if (step === 1) {
                const it = items[0];
                const imgs = it ? (Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : [])) : [];
                const cur = it && typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
                const imgSrc = imgs[cur] || it?.imageUrl || '';
                const hasAnyImage = imgs.length > 0 && String(imgSrc || '').trim().length > 0;
                if (!hasAnyImage) {
                  setShowTip('Dit product kan helaas niet mee in je FiT sessie. Probeer het met een ander product.');
                  setTimeout(() => setShowTip(null), 2500);
                  return;
                }
                const ok = await isImageLoadable(imgSrc);
                if (!ok) {
                  setShowTip('De productafbeelding kon niet worden geladen. Sluit dit venster en start FiT opnieuw vanaf een productpagina');
                  setTimeout(() => setShowTip(null), 10000);
                  return;
                }
              }
              if (!hasAnyUserPhoto) {
                setShowTip(L('Upload eerst een pasfoto voordat je naar Stijl gaat','Please upload a photo before going to Style'));
                setTimeout(() => setShowTip(null), 1800);
                return;
              }
              setStep(3);
            }}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-all ${
              step === 3 
                ? 'text-white bg-orange-500 border-b-2 border-orange-500' 
                : (!hasAnyUserPhoto ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400')
            }`}
            disabled={!hasAnyUserPhoto}
          >
            3 {L('Stijl','Style')}
          </button>
        </div>
        )}

        {/* Content */}
        {!resultUrl && (
        <div className="p-6 flex-1 overflow-auto">
        {step === 1 && (
          <>
            <div className="mb-6">
              <FitStepProgress currentStep={1} />
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{L('Jouw geselecteerde items','Your selected items')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {items.map((it, idx) => {
                  const imgs = Array.isArray(it.imageUrls) && it.imageUrls.length ? it.imageUrls : (it.imageUrl ? [it.imageUrl] : []);
                  const cur = typeof it.currentImageIndex === 'number' ? it.currentImageIndex : 0;
                  const imgSrc = imgs[cur] || it.imageUrl || '';
                  return (
                    <div key={idx} className="border-2 border-slate-200 rounded-xl p-3 hover:border-orange-300 transition-all bg-white relative">
                      <div className="relative mb-3 group">
                        {imgSrc ? (
                          <img src={imgSrc} alt={it.title} className="w-full aspect-square object-contain rounded-lg bg-white" />
                        ) : (
                          <div className="w-full aspect-square rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">{L('Geen afbeelding','No image')}</div>
                        )}
                        {/* Enlarge preview */}
                        {imgSrc && (
                          <button onClick={() => setPreviewUrl(imgSrc)} className="absolute top-2 left-2 bg-white/90 hover:bg-white text-slate-900 rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-all" aria-label={L('Vergroot afbeelding','Enlarge image')}>
                            <Maximize2 className="w-4 h-4" />
                          </button>
                        )}
                        {(isPortal || idx !== 0) && (
                          <button onClick={() => onRemoveItem(idx)} className={`absolute -top-2 -right-2 rounded-full p-1.5 transition-colors shadow-lg z-10 ${isPortal ? 'bg-white text-black border border-slate-300 hover:bg-slate-100' : 'bg-slate-900 text-white hover:bg-slate-700'}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <span className="absolute bottom-2 left-2 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">✓</span>
                        {imgs.length > 1 && (
                          <>
                            <button onClick={() => prevProductImage(idx)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-1.5 shadow-lg transition-all">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button onClick={() => nextProductImage(idx)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black rounded-full p-1.5 shadow-lg transition-all">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            <div className="absolute bottom-2 right-2 bg-slate-900/80 text-white text-xs px-2 py-0.5 rounded-full">
                              {cur + 1}/{imgs.length}
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-xs mb-1 line-clamp-2 leading-tight">{it.isUploaded ? L('niet beschikbaar','not available') : (it.title || 'Item')}</h4>
                        {it.isUploaded ? (
                          <p className="text-slate-500 text-xs">{L('niet beschikbaar','not available')}</p>
                        ) : (
                          it.price ? <p className="text-orange-600 font-bold text-sm">{it.price}</p> : null
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Add Item Card: for guest always render (even at limit) and open login on click */}
                {(() => {
                  const isGuest = !userEmail;
                  const atUserLimit = items.length >= itemsCap;
                  if (!isGuest && atUserLimit) return null;
                  return (
                    <div
                      onClick={() => {
                        if (isGuest) { openLogin(); return; }
                        setShowAdd(v => !v);
                      }}
                      className={`border-2 border-dashed rounded-xl p-3 transition-all group flex items-center justify-center min-h-[200px] cursor-pointer ${isGuest ? 'border-slate-200 bg-slate-50' : 'border-slate-300 hover:border-orange-400 hover:bg-orange-50/50'}`}
                    >
                      <div className="text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${isGuest ? 'bg-slate-100' : 'bg-slate-100 group-hover:bg-orange-100'}`}>
                          <svg className={`w-6 h-6 ${isGuest ? 'text-slate-300' : 'text-slate-400 group-hover:text-orange-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                        <p className={`text-xs font-semibold ${isGuest ? 'text-slate-400' : 'text-slate-700 group-hover:text-slate-900'}`}>{isGuest ? L('Login om meerdere items toe te voegen','Log in to add more items') : L('Voeg item toe','Add item')}</p>
                        <p className="text-xs text-slate-500 mt-1">{isGuest ? L('Slechts 1 item beschikbaar voor gast','Only 1 item for guests') : L(`Max ${itemsMaxFromConfig} items`, `Max ${itemsMaxFromConfig} items`)}</p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {showAdd && (
                <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 rounded-lg p-2 mt-0.5">
                      <Info className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <label className="form-label font-semibold">{L('Product URL','Product URL')}</label>
                      <input className="form-input" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://..." disabled={isScraping} />
                      <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-slate-600 flex items-center gap-2" style={{visibility: isScraping ? 'visible' : 'hidden'}}>
                          <span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-orange-500 rounded-full animate-spin"></span>
                          <span>{L('Item wordt opgehaald…','Fetching item…')}</span>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-none sm:gap-2">
                          <button className="btn btn-secondary w-full sm:w-auto" onClick={() => setShowAdd(false)} disabled={isScraping}>{L('Annuleren','Cancel')}</button>
                          <button className="btn btn-primary w-full sm:w-auto" onClick={onAddUrl} disabled={isScraping}>{L('Toevoegen','Add')}</button>
                          <button className="btn btn-secondary w-full sm:w-auto" onClick={guardedProductUploadClick} disabled={isUploadingProduct}>
                            {isUploadingProduct ? L('Uploaden…','Uploading…') : L('Upload afbeelding','Upload image')}
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-500">{L('Alleen PNG of JPG. Max 3 MB.','PNG or JPG only. Max 3 MB.')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tip Box (managed from settings; fallback to default) */}
              {(tipHeaderTitle || tipBodyText) ? (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-100 rounded-lg p-2 mt-0.5">
                      <Info className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 mb-1">{tipHeaderTitle || L('Tip voor beste resultaat','Tip for best result')}</h4>
                      {tipBodyText ? (<p className="text-sm text-amber-800 leading-relaxed">{tipBodyText}</p>) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 rounded-lg p-2 mt-0.5">
                      <Info className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-1">{L('Tip voor beste resultaat','Tip for best result')}</h4>
                      <p className="text-sm text-blue-800 leading-relaxed">{L('Selecteer een productafbeelding waar geen modellen op staan voor het beste resultaat','Select a product image without models for the best result')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3">
                <button className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 hover:border-slate-300 transition-all" onClick={onCloseModal}>{L('Annuleren','Cancel')}</button>
                <button className="btn btn-primary flex-1 flex items-center justify-center gap-2" onClick={onNext} disabled={!canNext}>
                  {L('Volgende','Next')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Footer CTA */}
              <div className="mt-6 flex justify-center text-sm text-slate-600">
                <span>{L('Wil jij dit ook op je webshop?','Want this on your webshop too?')} </span>&nbsp;
                <a className="text-orange-600 font-semibold hover:underline" href={homepageUrl} target="_blank" rel="noopener noreferrer">{L('Maak gratis kennis met FiT!','Get to know FiT for free!')}</a>
              </div>
            </div>

            {/* Removed duplicate navigation and CTA on step 1 */}
          </>
        )}

        <input ref={productFileInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onProductFileChange} />

        {step === 2 && (
          <>
            <div className="mb-6">
              <FitStepProgress currentStep={2} />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{L('Jouw pasfoto','Your photo')}</h3>
              <div className="mb-4 bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
                <div className="flex items-start gap-2 text-sm text-slate-800 select-none">
                  <input id="fit-consent" type="checkbox" checked={consentGiven} onChange={(e)=>onConsentChange(e.target.checked)} className="mt-1" />
                  <span>
                    {L('Ik ga akkoord met de','I agree with the')} <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{L('algemene voorwaarden','terms and conditions')}</a> {L('en','and')} <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{L('het privacybeleid','the privacy policy')}</a>.
                  </span>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {(
                  (photoSlots.length ? photoSlots : ['pasphoto_front','pasphoto_side','pasphoto_fullbody_front','pasphoto_fullbody_side']).slice(0, (userEmail ? 6 : 1))
                ).map((slotId) => {
                  const src = photoMap[slotId] || '';
                  return (
                    <div key={slotId} className="space-y-2">
                      <div
                        className={`${src ? 'aspect-[3/4] rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50 relative group' : 'aspect-[3/4] rounded-xl border-2 border-dashed border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors flex items-center justify-center'}`}
                        onClick={!src ? () => guardedUploadClick(slotId) : undefined}
                      >
                        {src ? (
                          <>
                            <img src={src} alt={`Pasfoto ${slotId}`} className="w-full h-full object-cover" />
                            <div className="absolute top-2 right-2 flex gap-2 z-10">
                              <button onClick={() => onDeleteSlot(slotId)} className="p-2 rounded-full bg-white/90 hover:bg-white shadow" aria-label={L('Verwijderen','Delete')}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                            {/* Altijd zichtbare actiebar onderin (geen hover nodig) */}
                            <div className="absolute bottom-4 left-4 right-4 flex gap-2 z-10">
                              <button type="button" onClick={(e) => { e.stopPropagation(); guardedUploadClick(slotId); }} disabled={!consentGiven} className={`flex-1 bg-white text-slate-900 px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${!consentGiven ? 'opacity-60 cursor-not-allowed' : 'hover:bg-slate-100'}`}>
                                <Camera className="w-4 h-4" />
                                {L('Vervang','Replace')}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-4 select-none">
                            <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                              <Camera className="w-6 h-6" />
                            </div>
                            <p className="mt-2 font-medium text-slate-800">{L('Upload je pasfoto','Upload your photo')}</p>
                            <p className="text-xs text-slate-500">{L('Sta recht, goede belichting, neutrale achtergrond','Stand straight, good lighting, neutral background')}</p>
                            <div className="mt-3">
                              <button type="button" onClick={(e) => { e.stopPropagation(); guardedUploadClick(slotId); }} disabled={!consentGiven} className={`bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium ${!consentGiven ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
                                {L('Upload foto','Upload photo')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-center text-xs text-slate-600">{getSlotLabel(slotId)}</p>
                    </div>
                  );
                })}
                {/* Info Box */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 md:col-span-3">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="bg-blue-100 rounded-lg p-2 mt-0.5">
                      <Info className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">{(infoHeader && infoHeader.trim()) ? infoHeader : (categoryInfoBox ? L('Categorie-informatie','Category information') : L('Tips voor de beste pasfoto','Tips for the best photo'))}</h4>
                      {(infoBody && infoBody.trim()) ? (
                        <p className="text-sm text-blue-800 leading-relaxed">{infoBody}</p>
                      ) : (categoryInfoBox ? (
                        <p className="text-sm text-blue-800 leading-relaxed">{categoryInfoBox}</p>
                      ) : (
                        <ul className="space-y-2 text-sm text-blue-800">
                          <li className="flex items-start gap-2"><span className="text-blue-600 mt-0.5">•</span><span>{L('Zorg voor goede verlichting van voren','Ensure good front lighting')}</span></li>
                          <li className="flex items-start gap-2"><span className="text-blue-600 mt-0.5">•</span><span>{L('Sta recht voor de camera','Stand straight in front of the camera')}</span></li>
                          <li className="flex items-start gap-2"><span className="text-blue-600 mt-0.5">•</span><span>{L('Draag effen, goed passende kleding','Wear plain, well-fitting clothing')}</span></li>
                          <li className="flex items-start gap-2"><span className="text-blue-600 mt-0.5">•</span><span>{L('Gebruik een neutrale achtergrond','Use a neutral background')}</span></li>
                        </ul>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Tip Box (optional) */}
                {false && (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 md:col-span-3">
                    <div className="flex items-start gap-3 mb-1">
                      <div className="bg-amber-100 rounded-lg p-2 mt-0.5">
                        <Info className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-amber-900 mb-2">{tipHeaderTitle || L('Tip voor beste resultaat','Tip for best result')}</h4>
                        {tipBodyText ? (
                          <p className="text-sm text-amber-800 leading-relaxed">{tipBodyText}</p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2" onClick={onPrev}>
                <ArrowLeft className="w-4 h-4" />
                {L('Vorige','Previous')}
              </button>
              <button className={`btn btn-primary flex-1 flex items-center justify-center gap-2`} onClick={onNext} disabled={!canNext}>
                {L('Volgende','Next')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Footer CTA */}
            <div className="mt-6 flex justify-center text-sm text-slate-600">
              <span>{L('Wil jij dit ook op je webshop?','Want this on your webshop too?')} </span>&nbsp;
              <a className="text-orange-600 font-semibold hover:underline" href={homepageUrl} target="_blank" rel="noopener noreferrer">{L('Maak gratis kennis met FiT!','Get to know FiT for free!')}</a>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="mb-6">
              <FitStepProgress currentStep={3} />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">{L('Kies je stijl','Choose your style')}</h3>
              {!userEmail && !limits?.guestCanChooseStyle && (
                <div className="text-xs text-slate-500 mb-3">{L('Log in om meerdere stijlen te kunnen kiezen.','Log in to choose multiple styles.')} <button className="text-orange-600 hover:underline font-semibold" onClick={openLogin}>{L('Log in','Log in')}</button></div>
              )}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(styleOptions.length ? styleOptions : [
                    { id: '1', name: L('Stijl 1','Style 1'), desc: L('Stijloptie 1','Style option 1') },
                    { id: '2', name: L('Stijl 2','Style 2'), desc: L('Stijloptie 2','Style option 2') },
                    { id: '3', name: L('Stijl 3','Style 3'), desc: L('Stijloptie 3','Style option 3') },
                    { id: '4', name: L('Stijl 4','Style 4'), desc: L('Stijloptie 4','Style option 4') }
                  ]).map((st, idx) => {
                    const iconList = [Sparkles, Coffee, Dumbbell, Briefcase];
                    const iconMap: Record<string, any> = {
                      sparkles: Sparkles,
                      'building-office': Building2,
                      briefcase: Briefcase,
                      cake: Cake,
                      camera: Camera,
                      'shopping-bag': ShoppingBag,
                      shoppingbag: ShoppingBag,
                      bag: ShoppingBag,
                      film: Film,
                      'globe-asia-australia': Globe,
                      globe: Globe,
                      home: Home,
                      photo: ImageIcon,
                      image: ImageIcon,
                      sun: Sun,
                      moon: Moon,
                      user: User,
                      'user-group': Users,
                      users: Users,
                      'video-camera': Video,
                      video: Video
                    };
                    const IconComp = (st as any).icon && iconMap[String((st as any).icon).toLowerCase()] ? iconMap[String((st as any).icon).toLowerCase()] : (iconList[idx % iconList.length] as any);
                    const colorKeys = ['purple','blue','green','slate'] as const;
                    const color = colorKeys[idx % colorKeys.length];
                    const isSelected = String(style) === String(st.id);
                    const colorClasses: any = {
                      purple: { bg: 'bg-purple-50', border: 'border-purple-200', iconBg: 'bg-purple-100', icon: 'text-purple-600', text: 'text-purple-900', selectedBorder: 'border-purple-500', selectedBg: 'bg-purple-100' },
                      blue: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-100', icon: 'text-blue-600', text: 'text-blue-900', selectedBorder: 'border-blue-500', selectedBg: 'bg-blue-100' },
                      green: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-100', icon: 'text-green-600', text: 'text-green-900', selectedBorder: 'border-green-500', selectedBg: 'bg-green-100' },
                      slate: { bg: 'bg-slate-50', border: 'border-slate-200', iconBg: 'bg-slate-100', icon: 'text-slate-600', text: 'text-slate-900', selectedBorder: 'border-slate-500', selectedBg: 'bg-slate-100' }
                    };
                    const colors = colorClasses[color];
                    const disabled = !userEmail && !limits?.guestCanChooseStyle && idx > 0;
                    const chipStyle = (st as any).color ? { borderColor: String((st as any).color) } as React.CSSProperties : undefined;
                    const iconTint = (st as any).color ? { color: String((st as any).color) } as React.CSSProperties : undefined;
                    return (
                      <button key={st.id} onClick={() => { if (!disabled) setStyle(String(st.id)); }} disabled={disabled} className={`text-left p-4 rounded-xl border-2 transition-all ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${isSelected ? `${colors.selectedBorder} ${colors.selectedBg} shadow-lg` : `${colors.border} ${colors.bg} hover:border-slate-300 hover:shadow-md`}`} style={chipStyle}>
                        <div className="flex items-start gap-3">
                          <div className={`${colors.iconBg} rounded-lg p-2.5 mt-0.5`}>
                            <IconComp className={`w-6 h-6 ${colors.icon}`} style={iconTint} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className={`font-bold ${colors.text} text-base`}>{(st as any).name || (st as any).label || `Stijl ${st.id}`}</h4>
                              {isSelected && (
                                <div className="bg-green-500 text-white rounded-full p-1">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">{(st as any).desc || L('Kies deze stijl voor jouw FiT resultaat.','Choose this style for your FiT result.')}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Info Box */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-100 rounded-lg p-2 mt-0.5">
                      <Info className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 mb-1">{L('Over stijlkeuzes','About style choices')}</h4>
                      <p className="text-sm text-amber-800 leading-relaxed">{L('Kies de stijl waarin je jezelf graag terug ziet op de FiT foto.','Choose the style you want to see yourself in on the FiT photo.')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2" onClick={onPrev}>
                <ArrowLeft className="w-4 h-4" />
                {L('Vorige','Previous')}
              </button>
              <button className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-500/30 flex items-center justify-center gap-2" onClick={onStartSession}>
                {L('Start FiT Sessie','Start FiT Session')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Footer CTA */}
            <div className="mt-6 flex justify-center text-sm text-slate-600">
              <span>{L('Wil jij dit ook op je webshop?','Want this on your webshop too?')} </span>&nbsp;
              <a className="text-orange-600 font-semibold hover:underline" href={homepageUrl} target="_blank" rel="noopener noreferrer">{L('Maak gratis kennis met FiT!','Get to know FiT for free!')}</a>
            </div>
          </>
        )}

        {/* Image Preview Overlay */}
        {previewUrl && (
          <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center" role="dialog" aria-modal="true" onClick={() => setPreviewUrl(null)}>
            <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e)=>e.stopPropagation()}>
              <img src={previewUrl} alt="Item preview" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
              <button onClick={() => setPreviewUrl(null)} className="absolute -top-3 -right-3 bg-white text-slate-900 rounded-full p-2 shadow" aria-label="Sluiten">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        

        </div>
        )}

        {/* Loading overlay with Lottie */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-white/90 flex flex-col items-center justify-center z-50">
            <div ref={lottieRef} className="w-36 h-36" />
            <div className="mt-4 text-slate-700 text-sm text-center px-6">{L('Een ogenblik, binnen enkele seconde hebben we je FiT gemaakt','One moment please, we are generating your FiT')}</div>
            <div className="sr-only">{L('Bezig met genereren...','Generating...')}</div>
          </div>
        )}

        {/* Result view (only) */}
        {!!resultUrl && !isSubmitting && (
          <div className="p-0 flex-1 overflow-auto">
            <div className="relative w-full flex items-center justify-center bg-transparent px-6 pt-6" style={{ minHeight: 'min(60vh, calc(100svh - 320px))' }}>
              <img
                src={PLACEHOLDER_URL}
                alt="FiT placeholder"
                className={`w-auto h-auto max-w-[92vw] object-contain rounded-xl ${resultImageLoaded ? 'hidden' : 'block'}`}
                style={{ maxHeight: 'min(60vh, calc(100svh - 320px))' }}
              />
              <img
                src={resultUrl}
                onLoad={() => setResultImageLoaded(true)}
                alt="FiT resultaat"
                className="w-auto h-auto max-w-[92vw] object-contain rounded-xl"
                style={{ maxHeight: 'min(60vh, calc(100svh - 320px))', display: resultImageLoaded ? 'block' : 'none' }}
              />
              <div className="absolute top-4 right-4 flex gap-2">
                {userEmail && (
                  <button aria-label="Favoriet" onClick={onToggleFavorite} className={`p-2 rounded-full shadow-lg transition-colors ${favorite ? 'bg-red-500 text-white' : 'bg-white/95 hover:bg-white text-slate-800'}`}>
                    <Heart className="w-5 h-5" />
                  </button>
                )}
                <button aria-label="Opnieuw" onClick={onRetryToStyle} className="p-2 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg">
                  <RotateCw className="w-5 h-5" />
                </button>
                <button aria-label="Delen" onClick={onShare} className="p-2 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg">
                  <Share2 className="w-5 h-5" />
                </button>
                <button aria-label="Download" onClick={onDownload} className="p-2 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg">
                  <DownloadIcon className="w-5 h-5" />
                </button>
                <button aria-label="Volledig scherm" onClick={onFullscreen} className="p-2 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg">
                  <Maximize2 className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 pb-8">
              <div className="text-center text-slate-700 mt-6 mb-3">
                {L('Wat vind je van dit resultaat? Deel je feedback hieronder.','What do you think of this result? Share your feedback below.')}
              </div>
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="text-sm text-slate-600">{L('Feedback:','Feedback:')}</span>
                <button
                  onClick={() => submitFeedbackApi(true, '')}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${satisfied === true ? 'bg-green-100 text-green-700 border-green-300' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  aria-label={L('Goed resultaat','Good result')}
                >
                  <ThumbsUp className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors ${satisfied === false ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  aria-label={L('Kan beter','Could be better')}
                >
                  <ThumbsDown className="w-5 h-5" />
                </button>
              </div>
              <div className="text-center mb-5">
                <a href={sessionsUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 font-semibold hover:underline">
                  {L('Bekijk al je FiT sessies','View all your FiT sessions')}
                </a>
              </div>
              <div className="text-center text-sm text-slate-600">
                <span>{L('Wil jij dit ook op je webshop?','Want this on your webshop too?')} </span>&nbsp;
                <a className="text-orange-600 font-semibold hover:underline" href={homepageUrl} target="_blank" rel="noopener noreferrer">{L('Maak gratis kennis met FiT!','Get to know FiT for free!')}</a>
              </div>
            </div>
          </div>
        )}

        {showFullscreen && !!resultUrl && (
          <div
            className="fixed inset-0 z-[10020] bg-black/90 flex items-center justify-center"
            onClick={onCloseFullscreen}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative max-w-[98vw] max-h-[98svh]" onClick={(e) => e.stopPropagation()}>
              <img src={resultUrl} alt="FiT resultaat (volledig scherm)" className="max-w-[98vw] max-h-[98svh] object-contain" />
              <button onClick={onCloseFullscreen} className="absolute top-4 right-4 p-2 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg" aria-label="Sluiten">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Negative feedback modal */}
        {showFeedbackModal && (
          <div
            className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowFeedbackModal(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {L('Vertel ons meer','Tell us more')}
                </h3>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="p-2 rounded hover:bg-gray-100"
                  aria-label="Sluiten"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {L('Wat kan er beter aan dit FiT resultaat?','What could be improved about this FiT result?')}
              </p>
              <textarea
                value={feedbackInput}
                onChange={(e) => setFeedbackInput(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={L('Jouw toelichting...','Your explanation...')}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                >
                  {L('Annuleren','Cancel')}
                </button>
                <button
                  onClick={onSubmitNegativeFeedback}
                  className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  {L('Versturen','Send')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global toast */}
        {showTip && (
          (typeof document !== 'undefined' && document?.body)
            ? createPortal(
                <div
                  className="fixed top-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-4 py-2 rounded-xl shadow pointer-events-none z-[2147483647]"
                  style={{ zIndex: 2147483647 }}
                  role="alert"
                  aria-live="polite"
                >
                  {showTip}
                </div>,
                document.body
              )
            : (
                <div
                  className="fixed top-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-4 py-2 rounded-xl shadow pointer-events-none z-[2147483647]"
                  style={{ zIndex: 2147483647 }}
                  role="alert"
                  aria-live="polite"
                >
                  {showTip}
                </div>
              )
        )}
      </div>
    </div>
  );
}

